/**
 * The email-safe call-to-action button.
 *
 * Every rule here exists because some mail client breaks the obvious version:
 *
 *   - a TABLE, not a styled <div>. Outlook (Word rendering engine) ignores
 *     padding and border-radius on inline elements; a one-cell table is the only
 *     shape that gets a reliable coloured box around a link.
 *   - a SOLID background declared BEFORE any gradient, on the same element.
 *     Clients that understand `linear-gradient` override the solid; the many
 *     that don't keep it and still render a filled button rather than bare text.
 *   - inline styles only. Nothing in an email may depend on a <style> block —
 *     Gmail strips them.
 *   - explicit `color` on the <a> AND on the cell, because several clients
 *     re-colour unstyled links to their own blue.
 *   - `mso-padding-alt` + a non-breaking-space pad so Outlook, which drops
 *     padding on the anchor, still gets a button-shaped hit area.
 *
 * Kept in shared/ so the editor that inserts it and any test that checks it are
 * looking at the same string.
 */

export interface CtaOptions {
  label: string
  url: string
  /** solid fallback + gradient start; defaults to the app's accent */
  color?: string
  /** gradient end; defaults to a lighter accent */
  colorTo?: string
  align?: 'left' | 'center' | 'right'
}

const ACCENT = '#5b5bd6'
const ACCENT_TO = '#7d7bea'

function esc(s: string): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * A URL we are willing to put behind a button. Anything that is not http(s) or
 * mailto — `javascript:` above all — is dropped to '#', so a pasted payload
 * cannot become a live link in someone's inbox.
 */
export function safeCtaUrl(raw: string): string {
  const url = String(raw ?? '').trim()
  if (!url) return '#'
  if (/^https?:\/\//i.test(url) || /^mailto:/i.test(url)) return url
  // A bare domain is a typo, not an attack — assume https.
  if (/^[\w-]+(\.[\w-]+)+([/?#].*)?$/.test(url)) return `https://${url}`
  return '#'
}

/** The button, as one line of email-safe HTML. */
export function buildCtaHtml(opts: CtaOptions): string {
  const label = esc(opts.label?.trim() || 'Click here')
  const href = esc(safeCtaUrl(opts.url))
  const color = opts.color ?? ACCENT
  const colorTo = opts.colorTo ?? ACCENT_TO
  const align = opts.align ?? 'left'

  // NOTE: `background` twice on purpose — see the header comment.
  const cell =
    `background:${color};` +
    `background:linear-gradient(135deg,${color},${colorTo});` +
    'border-radius:8px;' +
    'mso-padding-alt:12px 22px;'

  const anchor =
    'display:inline-block;' +
    'padding:12px 22px;' +
    'font-family:-apple-system,\'Segoe UI\',Roboto,Helvetica,Arial,sans-serif;' +
    'font-size:15px;font-weight:600;line-height:1.2;' +
    `color:#ffffff;text-decoration:none;` +
    'mso-text-raise:2px;'

  return (
    `<table role="presentation" border="0" cellpadding="0" cellspacing="0" ` +
    `style="border-collapse:separate;margin:16px 0;" align="${align}" data-lm-cta="1">` +
    `<tr><td style="${cell}">` +
    `<a href="${href}" target="_blank" rel="noopener" style="${anchor}">${label}</a>` +
    `</td></tr></table>`
  )
}

/** True when the markup contains at least one button built by buildCtaHtml. */
export function hasCta(html: string): boolean {
  return /data-lm-cta="1"/.test(html ?? '')
}
